import { useState } from 'react';
import { Download, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { exportAccountData, deleteAccount } from '../lib/api';
import { friendlyError } from '../lib/errorReporting';

// Settings → "Tus datos": download everything as JSON, or delete the
// account for good. Both are promised in the privacy policy
// (mipisto.net/privacidad) and expected by Google's review of the Gmail
// integration.
export default function AccountDataCard({ onDeleted }) {
  const [exporting, setExporting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [password, setPassword] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await exportAccountData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mipisto-mis-datos-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudieron descargar tus datos');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (!password) return;
    setDeleting(true);
    try {
      await deleteAccount(password);
      toast.success('Tu cuenta fue eliminada');
      onDeleted();
    } catch (e) {
      toast.error(friendlyError(e, 'No se pudo eliminar la cuenta'));
      setDeleting(false);
    }
  };

  return (
    <div className="glass-card rounded-2xl p-6" data-testid="account-data-card">
      <h3 className="text-lg font-medium text-white mb-1 flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-violet-400" />
        Tus datos
      </h3>
      <p className="text-sm text-slate-400 mb-5">
        Tus datos son tuyos. Descárgalos cuando quieras o elimina tu cuenta.{' '}
        <a href="https://mipisto.net/privacidad" target="_blank" rel="noreferrer" className="text-violet-400 hover:text-violet-300">
          Cómo tratamos tus datos
        </a>
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting}
          data-testid="export-data-btn"
          className="hit-44 h-10 px-4 rounded-xl flex items-center justify-center gap-2 bg-white/[0.06] hover:bg-white/10 text-slate-200 text-sm font-medium transition-colors active:scale-[0.98] disabled:opacity-50"
        >
          <Download className="w-4 h-4" /> {exporting ? 'Preparando…' : 'Descargar mis datos'}
        </button>
        <button
          type="button"
          onClick={() => { setPassword(''); setShowDelete(true); }}
          data-testid="delete-account-btn"
          className="hit-44 h-10 px-4 rounded-xl flex items-center justify-center gap-2 text-rose-400 hover:bg-rose-500/10 text-sm font-medium transition-colors active:scale-[0.98]"
        >
          <Trash2 className="w-4 h-4" /> Eliminar mi cuenta
        </button>
      </div>

      <Dialog open={showDelete} onOpenChange={(o) => { if (!deleting) setShowDelete(o); }}>
        <DialogContent className="bg-slate-900 border-white/10 text-slate-200 rounded-2xl max-w-sm" data-testid="delete-account-dialog">
          <DialogTitle className="text-lg font-semibold text-white">¿Eliminar tu cuenta?</DialogTitle>
          <DialogDescription asChild>
            <div className="text-sm text-slate-400 space-y-2">
              <p>Se borran para siempre tus transacciones, categorías, presupuestos, metas personales, conversaciones con el Coach y la conexión con Gmail. <span className="text-slate-200">No se puede deshacer.</span></p>
              <p>Si participas en metas compartidas, tus aportes salen de ellas; si creaste una con otras personas, pasa a otro participante.</p>
              <p>Si quieres, descarga tus datos antes.</p>
            </div>
          </DialogDescription>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Tu contraseña para confirmar"
            autoComplete="current-password"
            data-testid="delete-account-password"
            className="w-full h-11 mt-2 rounded-xl bg-slate-950/50 border border-slate-700 px-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-rose-500/50"
          />
          <div className="flex gap-2 mt-2">
            <button type="button" onClick={() => setShowDelete(false)} disabled={deleting}
              className="flex-1 h-11 rounded-xl text-sm font-medium text-slate-300 hover:bg-white/5">
              Cancelar
            </button>
            <button type="button" onClick={handleDelete} disabled={!password || deleting}
              data-testid="delete-account-confirm"
              className="flex-1 h-11 rounded-xl text-sm font-semibold bg-rose-500 hover:bg-rose-600 text-on-brand disabled:opacity-40">
              {deleting ? 'Eliminando…' : 'Eliminar para siempre'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
